import React, { useEffect, useMemo, useState } from "react";
import {
  arrayUnion,
  collection,
  doc,
  getDocs,
  query,
  runTransaction,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { auth, db } from "../../../firebaseConfig";
import {
  WORK_STAGES,
  availableStages,
  canAllocateStage,
  dedupeStageTasks,
  isMouExecuted,
  loadProjectTasks,
  queueWorkflowMail,
  syncEligibleNafedProjects,
  upper,
} from "./nafedWorkflowBridge";
import { NAFED_COLLECTIONS } from "./nafedCollections";

const statusClass = (value) => {
  const v = upper(value);
  if (v === "APPROVED") return "ok";
  if (["REJECTED", "QUERY_RAISED"].includes(v)) return "bad";
  if (["SUBMITTED", "COMPLETED", "UNDER_PMC_REVIEW"].includes(v))
    return "review";
  return "pending";
};

const stageLabel = (value) =>
  WORK_STAGES.find((item) => item.key === upper(value))?.label ||
  String(value || "-").replaceAll("_", " ");

const NEW_CSS = `
.nwf{background:#f4f7fa!important;padding:20px!important;border-radius:18px!important;max-width:1480px;margin:auto}.nwf *{box-sizing:border-box}
.nwf>header{padding:22px 24px!important;border-radius:14px!important;border-bottom:4px solid #d39a2c!important;box-shadow:0 12px 28px #10233b25!important}.nwf h2{font-size:24px!important}.notice{border-left:5px solid #197343!important;border-radius:0!important;font-weight:700}
.stats{gap:12px!important;margin:14px 0!important}.stats div{padding:15px!important;border-radius:10px!important;border-top:3px solid #1b6b43!important}.stats span{font-weight:800!important;text-transform:uppercase}.stats b{font-size:24px!important;color:#0d2d56!important}
.body{display:block!important;border-color:#9eabb9!important;border-radius:12px!important;overflow:hidden!important;box-shadow:0 10px 30px #10233b12}.body aside{display:flex!important;align-items:center!important;gap:10px!important;padding:12px 16px!important;border:0!important;border-bottom:1px solid #b8c3cf!important;background:#e9eef3!important;overflow-x:auto}.body aside h3{font-size:12px!important;min-width:max-content!important;margin:0 10px 0 0!important;text-transform:uppercase!important;letter-spacing:.07em!important;color:#16385f!important}.body aside>button{width:auto!important;min-width:225px!important;margin:0!important;padding:10px 13px!important;border:1px solid #a9b5c2!important;border-left:4px solid transparent!important;border-radius:6px!important;box-shadow:none!important}.body aside>button.active{border:1px solid #287d50!important;border-left:5px solid #287d50!important;background:#edf8f0!important}.body main{padding:16px 18px 20px!important}.project-head{padding:18px 20px!important;border-radius:8px!important;border-left:5px solid #d6a12f!important}.project-head h3{font-size:21px!important}
.timeline{gap:8px!important;margin:14px 0!important}.timeline>div{border-radius:8px!important}.duplicate-alert{display:flex;justify-content:space-between;align-items:center;padding:12px 14px;margin:10px 0;border:1px solid #d89332;background:#fff5e4}.duplicate-alert b,.duplicate-alert span{display:block}.duplicate-alert span{margin-top:3px;color:#7b5a25;font-size:11px}.duplicate-alert button{border:0;padding:9px 12px;background:#b6680c;color:#fff;font-weight:850}
.allocation-zone{margin:12px 0;border:1px solid #b7c3cf;border-radius:9px;background:#f5f8fb;overflow:hidden}.zone-title{padding:13px 15px;background:#e3eaf1}.zone-title h3{margin:3px 0;color:#0d2d56}.zone-title p{margin:0;color:#607286;font-size:11px}.allocation-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:10px;padding:12px}.allocation-card{display:grid;gap:8px;padding:13px;background:#fff;border:1px solid #b8c3cf;border-top:3px solid #1f7949;border-radius:8px}.allocation-card small{color:#1b7648;font-weight:900}.allocation-card h4{margin:3px 0;font-size:16px}.allocation-card select{width:100%;padding:10px;border:1px solid #9eabb9;border-radius:6px}.allocation-card button{border:0;border-radius:6px;padding:10px;background:#173b69;color:#fff;font-weight:900}.allocation-card button:disabled{opacity:.45}
.register-head{display:flex;justify-content:space-between;align-items:center;gap:14px;margin-top:16px;padding:13px 16px;background:#17385f;color:#fff;border:1px solid #0d2848;border-radius:8px 8px 0 0}.register-head small{display:block;color:#bfe1ca;font-weight:900;letter-spacing:.08em}.register-head h3{margin:3px 0 0;font-size:17px}.register-head span{font-size:11px;font-weight:800;background:#fff;color:#17385f;border-radius:999px;padding:7px 11px;white-space:nowrap}.task-table{margin-top:0!important;border:1px solid #26384b!important;border-top:0!important;border-radius:0 0 8px 8px!important;overflow-x:auto!important;background:#fff}.task-table table{width:100%!important;min-width:1050px!important;table-layout:fixed!important;border-collapse:collapse!important}.task-table th,.task-table td{border:1px solid #7f8b98!important;vertical-align:middle!important;overflow-wrap:anywhere}.task-table th{padding:11px 10px!important;background:#dce4ec!important;color:#102b4f!important;text-transform:uppercase!important;font-size:10px!important;letter-spacing:.055em!important;border-top:0!important}.task-table td{padding:12px 10px!important;background:#fff!important;font-size:12px!important;line-height:1.45!important}.task-table tbody tr:nth-child(even) td{background:#f7f9fb!important}.task-table tbody tr:hover td{background:#eef5f0!important}.stage-cell{border-left:4px solid #1f7548!important}.stage-code{display:block;color:#68788a;font-size:9px;font-weight:800;letter-spacing:.06em}.stage-name{display:block;margin-top:3px;color:#102b4f;font-size:13px;font-weight:950}.vendor-cell b{display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;color:#142d4d;font-size:12px;line-height:1.4}.task-table td small{display:block;margin-top:5px;color:#65778a;font-size:10px}.task-table td em{display:block;color:#8793a1;font-size:10px;line-height:1.4}.task-table td select,.task-table td input{width:100%;min-height:36px;padding:7px 9px;border:1px solid #8d9bab;border-radius:4px;background:#fff}.task-status{display:inline-flex!important;align-items:center;max-width:100%;padding:6px 8px!important;border:1px solid currentColor!important;border-radius:999px!important;font-size:9px!important;font-weight:900!important;white-space:normal;text-align:center;line-height:1.2}.task-status.ok{background:#e9f7ed;color:#17653e}.task-status.review{background:#eaf2fd;color:#174f91}.task-status.pending{background:#fff6df;color:#8b5b08}.task-status.bad{background:#ffedeb;color:#a3261c}.file-line{display:flex;justify-content:space-between;gap:6px;align-items:center;padding:4px 0;border-bottom:1px dashed #ccd4dc}.file-line:last-child{border-bottom:0}.file-line a{color:#0b5cab;font-size:10px;font-weight:800}.file-line button{min-width:22px!important;padding:2px 6px!important;background:#b42318!important;border-radius:4px!important}.edit-fields{display:grid;gap:6px}.row-actions{min-width:0!important}.action-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:5px}.action-grid button{width:100%!important;min-height:31px!important;margin:0!important;padding:5px 6px!important;border-radius:4px!important;font-size:10px!important;font-weight:850!important}.action-grid .approve{grid-column:1/-1;background:#187445!important}.action-grid button:disabled{opacity:.38!important;cursor:not-allowed!important}.task-table .edit{background:#205ea8!important}.task-table .delete{background:#b42318!important}.task-table .neutral{background:#64748b!important}.task-table .query{background:#ba6b12!important}.task-table .reject{background:#a92c22!important}.table-note{display:flex;justify-content:space-between;gap:10px;padding:9px 12px;background:#f3f6f9;border-top:1px solid #9eabb8;color:#5c6f82;font-size:10px;font-weight:700}
.pacs-register{margin-top:15px;background:#fff;border:1px solid #9facba;border-radius:10px;overflow:hidden;box-shadow:0 12px 30px #10233b14}.pacs-register-head{display:flex;justify-content:space-between;align-items:center;gap:15px;padding:15px 18px;background:#17385f;color:#fff;border-bottom:3px solid #d3a13b}.pacs-register-head small{display:block;color:#bfe4cc;font-size:9px;font-weight:900;letter-spacing:.1em}.pacs-register-head h3{margin:4px 0 0;font-size:18px}.pacs-register-head>b{padding:7px 10px;border:1px solid #8297af;border-radius:999px;font-size:10px}.pacs-filters{display:grid;grid-template-columns:minmax(300px,1fr) 220px 230px;gap:10px;padding:13px;background:#eef2f6;border-bottom:1px solid #a9b5c1}.pacs-filters input,.pacs-filters select{width:100%;height:40px;padding:0 11px;border:1px solid #69798a;border-radius:5px;background:#fff;color:#142d4d;font-weight:700}.pacs-table-wrap{overflow-x:auto}.pacs-table{width:100%;min-width:1000px;border-collapse:collapse;table-layout:fixed}.pacs-table th{padding:11px 10px;background:#dce4ec;color:#102b4f;border:1px solid #7b8794;text-align:left;text-transform:uppercase;font-size:9px;letter-spacing:.06em}.pacs-table td{padding:11px 10px;border:1px solid #8995a2;font-size:11px;vertical-align:middle}.pacs-table tbody tr:nth-child(even) td{background:#f7f9fb}.pacs-table tbody tr:hover td{background:#eef6f1}.pacs-table th:nth-child(1){width:5%}.pacs-table th:nth-child(2){width:24%}.pacs-table th:nth-child(3){width:18%}.pacs-table th:nth-child(4){width:11%}.pacs-table th:nth-child(5){width:14%}.pacs-table th:nth-child(6){width:17%}.pacs-table th:nth-child(7){width:11%}.pacs-table td strong,.pacs-table td small{display:block}.pacs-table td strong{font-size:12px;color:#112f55}.pacs-table td small{margin-top:4px;color:#65778a;font-size:9px}.registry-stage,.registry-status{display:inline-flex;padding:5px 7px;border:1px solid #6d7f91;border-radius:999px;background:#f3f6f9;color:#183b65;font-size:8px;font-weight:900;text-transform:uppercase}.registry-status{background:#fff3d9;color:#79510c;border-color:#ad7b21}.open-workflow{width:100%;min-height:34px;border:0;border-radius:5px;background:#176d43;color:#fff;font-size:10px;font-weight:900}.open-workflow:disabled{opacity:.5}.pacs-pagination{display:flex;justify-content:space-between;align-items:center;gap:12px;padding:11px 14px;background:#f2f5f8;color:#53677c;font-size:10px;font-weight:800}.pacs-pagination>div{display:flex;align-items:center;gap:6px}.pacs-pagination button{min-height:31px;padding:0 10px;border:1px solid #728195;border-radius:4px;background:#fff;color:#17385f;font-size:9px;font-weight:850}.pacs-pagination button:disabled{opacity:.4}.workflow-modal{position:fixed;inset:0;z-index:9999;display:grid;place-items:center;padding:24px}.modal-backdrop{position:absolute;inset:0;width:100%;height:100%;border:0;background:rgba(2,17,35,.76);backdrop-filter:blur(5px)}.workflow-window{position:relative;z-index:1;width:min(1380px,96vw);max-height:92vh;overflow:auto;padding:0 18px 20px!important;border:1px solid #a7b3bf;border-radius:12px;background:#f5f7fa;box-shadow:0 35px 100px rgba(0,0,0,.45)}.window-bar{position:sticky;top:0;z-index:20;display:flex;justify-content:space-between;align-items:center;margin:0 -18px 14px;padding:12px 18px;background:#102e55;color:#fff;border-bottom:3px solid #d3a13b}.window-bar small,.window-bar b{display:block}.window-bar small{color:#bde1c8;font-size:9px;font-weight:900;letter-spacing:.09em}.window-bar b{margin-top:3px;font-size:16px}.window-bar button{border:1px solid #fff;border-radius:5px;background:#fff;color:#17385f;padding:8px 12px;font-weight:900}
@media(max-width:1100px){.task-table table{min-width:1000px!important}.allocation-grid{grid-template-columns:1fr}.timeline{grid-template-columns:repeat(3,1fr)!important}}@media(max-width:700px){.nwf{padding:10px!important}.stats,.timeline{grid-template-columns:1fr!important}.register-head{align-items:flex-start;flex-direction:column}.table-note{flex-direction:column}}
`;

export default function NafedPmcWorkflowPanel({
  projects: sourceProjects = [],
  nafedScope = {},
  scope = {},
}) {
  const [projects, setProjects] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [tasksByProject, setTasksByProject] = useState({});
  const [duplicatesByProject, setDuplicatesByProject] = useState({});
  const [selectedId, setSelectedId] = useState("");
  const [vendorChoice, setVendorChoice] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [notice, setNotice] = useState("");
  const [editingId, setEditingId] = useState("");
  const [projectBusy, setProjectBusy] = useState("");
  const [pacsSearch, setPacsSearch] = useState("");
  const [pacsDistrict, setPacsDistrict] = useState("ALL");
  const [pacsStatus, setPacsStatus] = useState("ALL");
  const [page, setPage] = useState(1);
  const [editDraft, setEditDraft] = useState({
    vendorId: "",
    plannedDate: "",
    pmcRemarks: "",
  });

  const effectiveScope = Object.keys(nafedScope || {}).length
    ? nafedScope
    : scope || {};
  const sourceProjectKey = sourceProjects
    .map(
      (item) =>
        `${item?.id || ""}:${isMouExecuted(item) ? "EXECUTED" : "PENDING"}:${
          item?.updatedAt?.seconds || item?.updatedAt || ""
        }`,
    )
    .filter(Boolean)
    .sort()
    .join("|");

  const assertCurrentPmc = (project = {}) => {
    const user = auth.currentUser;
    if (!user) throw new Error("PMC login session has expired.");
    const assignedPmcUid = String(
      project.allocatedPmcUid || project.assignedPmcUid || project.pmcUid || "",
    ).trim();
    if (assignedPmcUid && assignedPmcUid !== user.uid) {
      throw new Error("This PACS project is not assigned to your PMC.");
    }
    return user;
  };

  async function load() {
    setLoading(true);
    setNotice("");
    try {
      const user = auth.currentUser;
      if (!user) throw new Error("PMC login session has expired.");
      const allowedIds = new Set(
        (
          effectiveScope.allowedProjectIds ||
          effectiveScope.assignedProjectIds ||
          sourceProjects.map((item) => item.id)
        )
          .map((value) => String(value || "").trim())
          .filter(Boolean),
      );

      await syncEligibleNafedProjects(db, {
        sourceProjects: sourceProjects.filter(
          (project) => !allowedIds.size || allowedIds.has(project.id),
        ),
        pmcUid: user.uid,
      });

      const [projectSnap, vendorSnap] = await Promise.all([
        getDocs(
          query(
            collection(db, NAFED_COLLECTIONS.workflowProjects),
            where("nafedProject", "==", true),
            where(
              "allocatedPmcUid",
              "==",
              user.uid,
            ),
          ),
        ),
        getDocs(collection(db, NAFED_COLLECTIONS.workVendors)),
      ]);
      const mine = projectSnap.docs
        .map((item) => ({ id: item.id, ...item.data() }))
        .filter(
          (item) =>
            item.syncStatus !== "DELETED" &&
            isMouExecuted(item) &&
            (!allowedIds.size || allowedIds.has(item.sourceProjectId || item.id)),
        );
      const approvedVendors = vendorSnap.docs
        .map((item) => ({ id: item.id, ...item.data() }))
        .filter(
          (item) =>
            item.isDeleted !== true &&
            item.isActive !== false &&
            upper(item.status || item.registrationStatus) === "APPROVED" &&
            ["SHINE_AGRI", "WORKFLOW_ADMIN"].includes(
              upper(item.controlledByPortal || "WORKFLOW_ADMIN"),
            ),
        );
      setProjects(mine);
      setVendors(approvedVendors);
      setSelectedId((current) =>
        current && mine.some((item) => item.id === current) ? current : "",
      );
    } catch (error) {
      setNotice(error.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // Source ids change only when the dashboard's PACS allocation changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceProjectKey]);

  async function loadProjectDetail(projectId) {
    if (!projectId) return;
    setProjectBusy(projectId);
    try {
      const result = dedupeStageTasks(await loadProjectTasks(db, projectId));
      setTasksByProject((old) => ({ ...old, [projectId]: result.tasks }));
      setDuplicatesByProject((old) => ({
        ...old,
        [projectId]: result.duplicates,
      }));
    } catch (error) {
      setNotice(error.message || "PACS workflow loading failed.");
    } finally {
      setProjectBusy("");
    }
  }

  async function openProject(projectId) {
    setSelectedId(projectId);
    setEditingId("");
    setNotice("");
    await loadProjectDetail(projectId);
  }

  const districts = useMemo(
    () =>
      [...new Set(projects.map((item) => item.district).filter(Boolean))].sort(
        (a, b) => a.localeCompare(b),
      ),
    [projects],
  );

  const filteredProjects = useMemo(() => {
    const needle = pacsSearch.trim().toLowerCase();
    return projects.filter((item) => {
      const text =
        `${item.pacsName || ""} ${item.applicationId || ""} ${item.district || ""} ${item.taluka || ""} ${item.locationVillage || item.village || ""} ${item.capacityMt || ""}`.toLowerCase();
      const districtOk =
        pacsDistrict === "ALL" || item.district === pacsDistrict;
      const statusValue = upper(
        item.technicalWorkflowStatus || item.currentTechnicalStage || "PENDING",
      );
      const statusOk =
        pacsStatus === "ALL" ||
        (pacsStatus === "READY" && statusValue.includes("READY")) ||
        (pacsStatus === "IN_PROGRESS" && !statusValue.includes("READY"));
      return (!needle || text.includes(needle)) && districtOk && statusOk;
    });
  }, [projects, pacsSearch, pacsDistrict, pacsStatus]);

  const PAGE_SIZE = 25;
  const totalPages = Math.max(
    1,
    Math.ceil(filteredProjects.length / PAGE_SIZE),
  );
  const safePage = Math.min(page, totalPages);
  const pagedProjects = filteredProjects.slice(
    (safePage - 1) * PAGE_SIZE,
    safePage * PAGE_SIZE,
  );

  const selected = projects.find((item) => item.id === selectedId) || null;
  const selectedTasks = selected ? tasksByProject[selected.id] || [] : [];
  const selectedDuplicates = selected
    ? duplicatesByProject[selected.id] || []
    : [];
  const unlockedStages = availableStages(selectedTasks);
  const vendorsForStage = (stageKey) =>
    vendors.filter((vendor) =>
      (vendor.approvedCategories || vendor.categories || [])
        .map(upper)
        .includes(stageKey),
    );

  async function allocate(stage) {
    if (!selected || !stage) return;
    let user;
    try {
      user = assertCurrentPmc(selected);
    } catch (error) {
      return setNotice(error.message);
    }
    const vendorId = vendorChoice[stage.key];
    const vendor = vendors.find((item) => item.id === vendorId);
    if (!vendor) return setNotice("Approved vendor select karo.");
    if (!canAllocateStage(selectedTasks, stage.key)) {
      return setNotice(
        "Previous stage PMC approval ke bina next work allocate nahi ho sakta.",
      );
    }
    setBusy(`allocate-${stage.key}`);
    try {
      const taskId = `${selected.id}_${stage.key}`;
      const taskRef = doc(db, NAFED_COLLECTIONS.workflowTasks, taskId);
      await runTransaction(db, async (transaction) => {
        const existing = await transaction.get(taskRef);
        if (existing.exists()) {
          const record = existing.data();
          const inactive =
            record.isDeleted === true ||
            ["DELETED", "ARCHIVED", "REMOVED"].includes(upper(record.status));
          if (!inactive) {
            throw new Error(
              `${stage.label} already has an active allocation. Refresh and review it.`,
            );
          }
        }
        transaction.set(taskRef, {
          projectId: selected.id,
          sourceProjectId: selected.sourceProjectId || selected.id,
          nafedProject: true,
          allocatedPmcUid: user.uid,
          pacsUid: selected.pacsUid || "",
          pacsName: selected.pacsName || "",
          type: stage.key,
          vendorId: vendor.id,
          vendorName: vendor.firmName || vendor.name || "",
          vendorEmail: vendor.email || "",
          status: "ASSIGNED",
          pmcStatus: "ALLOCATED",
          adminStatus: "PENDING",
          assignedByUid: user.uid,
          assignedByRole: "PMC",
          assignedAt: serverTimestamp(),
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          isDeleted: false,
        });
      });
      await updateDoc(
        doc(db, NAFED_COLLECTIONS.workflowProjects, selected.id),
        {
        currentTechnicalStage: stage.key,
        technicalWorkflowStatus: `${stage.key}_ALLOCATED`,
        assignedVendorIds: arrayUnion(vendor.id),
        updatedAt: serverTimestamp(),
        },
      );
      await queueWorkflowMail(db, {
        to: vendor.email,
        subject: `NAFED ${stage.label} work allocated — ${selected.pacsName}`,
        html: `<p>${vendor.name || vendor.firmName},</p><p>NAFED PACS <b>${selected.pacsName}</b> ke liye <b>${stage.label}</b> work allocate hua hai.</p><p><a href="${selected.googleMapsUrl}">Open Google Maps Site Location</a></p><p>Login: ${window.location.origin}/workflow/vendor</p>`,
        event: "NAFED_VENDOR_WORK_ALLOCATED",
        projectId: selected.id,
      });
      setNotice(`${stage.label} work allocated. Vendor email queued.`);
      await Promise.all([load(), loadProjectDetail(selected.id)]);
    } catch (error) {
      setNotice(error.message);
    } finally {
      setBusy("");
    }
  }

  async function decide(task, decision) {
    let user;
    try {
      user = assertCurrentPmc(selected);
    } catch (error) {
      return setNotice(error.message);
    }
    const reason =
      decision === "APPROVED"
        ? "Approved by allocated PMC"
        : window.prompt(`${decision} remarks:`, "") || "";
    if (!reason.trim()) return;
    if (!task.files?.length && decision === "APPROVED")
      return setNotice(
        "Vendor document upload ke bina approve nahi kar sakte.",
      );
    setBusy(task.id);
    try {
      await updateDoc(doc(db, NAFED_COLLECTIONS.workflowTasks, task.id), {
        pmcStatus: decision,
        adminStatus: decision,
        status: decision === "APPROVED" ? "APPROVED" : decision,
        pmcRemarks: reason.trim(),
        reviewedByPmcUid: user.uid,
        reviewedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      const updates = {
        technicalWorkflowStatus: `${task.type}_${decision}`,
        updatedAt: serverTimestamp(),
      };
      if (upper(task.type) === "DPR_CA" && decision === "APPROVED") {
        const amount = Number(
          task.finalAmount || task.submittedFinalAmount || 0,
        );
        if (amount <= 0) throw new Error("DPR final amount missing hai.");
        Object.assign(updates, {
          currentTechnicalStage: "READY_FOR_TENDER",
          technicalStage: "READY_FOR_TENDER",
          finalDprAmount: amount,
          approvedDprAmount: amount,
          tenderEstimatedValue: amount,
          tenderBaseAmount: amount,
          tenderValueLockedFromDpr: true,
        });
      }
      await updateDoc(
        doc(db, NAFED_COLLECTIONS.workflowProjects, selected.id),
        updates,
      );
      await queueWorkflowMail(db, {
        to: task.vendorEmail,
        subject: `NAFED ${task.type} submission ${decision}`,
        html: `<p>Your submission for <b>${selected.pacsName}</b> is <b>${decision}</b>.</p><p>${reason}</p>`,
        event: "NAFED_VENDOR_SUBMISSION_DECISION",
        projectId: selected.id,
      });
      setNotice(`Submission ${decision}.`);
      await Promise.all([load(), loadProjectDetail(selected.id)]);
    } catch (error) {
      setNotice(error.message);
    } finally {
      setBusy("");
    }
  }

  async function refreshVendorIndex(projectId) {
    const remaining = dedupeStageTasks(
      await loadProjectTasks(db, projectId),
    ).tasks;
    await updateDoc(doc(db, NAFED_COLLECTIONS.workflowProjects, projectId), {
      assignedVendorIds: [
        ...new Set(remaining.map((item) => item.vendorId).filter(Boolean)),
      ],
      updatedAt: serverTimestamp(),
    });
  }

  function startEdit(task) {
    if (upper(task.pmcStatus || task.status) === "APPROVED") {
      return setNotice(
        "Approved stage locked hai. Approved record edit nahi hoga.",
      );
    }
    setEditingId(task.id);
    setEditDraft({
      vendorId: task.vendorId || "",
      plannedDate: task.plannedDate || task.plannedDateTime || "",
      pmcRemarks: task.pmcRemarks || "",
    });
  }

  async function saveEdit(task) {
    let user;
    try {
      user = assertCurrentPmc(selected);
    } catch (error) {
      return setNotice(error.message);
    }
    const vendor = vendors.find((item) => item.id === editDraft.vendorId);
    if (!vendor) return setNotice("Valid approved vendor select karo.");
    setBusy(task.id);
    try {
      await updateDoc(doc(db, NAFED_COLLECTIONS.workflowTasks, task.id), {
        vendorId: vendor.id,
        vendorName: vendor.firmName || vendor.name || "",
        vendorEmail: vendor.email || "",
        plannedDate: editDraft.plannedDate || "",
        plannedDateTime: editDraft.plannedDate || "",
        pmcRemarks: editDraft.pmcRemarks.trim(),
        editedByPmcUid: user.uid,
        editedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      await updateDoc(
        doc(db, NAFED_COLLECTIONS.workflowProjects, selected.id),
        {
        assignedVendorIds: arrayUnion(vendor.id),
        updatedAt: serverTimestamp(),
        },
      );
      await refreshVendorIndex(selected.id);
      setEditingId("");
      setNotice("Vendor, visit date and remarks updated.");
      await Promise.all([load(), loadProjectDetail(selected.id)]);
    } catch (error) {
      setNotice(error.message);
    } finally {
      setBusy("");
    }
  }

  async function removeTask(task) {
    let user;
    try {
      user = assertCurrentPmc(selected);
    } catch (error) {
      return setNotice(error.message);
    }
    if (upper(task.pmcStatus || task.status) === "APPROVED") {
      return setNotice("Approved stage delete nahi kar sakte.");
    }
    if (
      !window.confirm(
        `${task.type} allocation archive karke re-allocation allow karein?`,
      )
    )
      return;
    setBusy(task.id);
    try {
      await updateDoc(doc(db, NAFED_COLLECTIONS.workflowTasks, task.id), {
        isDeleted: true,
        status: "ARCHIVED",
        archivedAt: serverTimestamp(),
        archivedByUid: user.uid,
        archivedByRole: "PMC",
        updatedAt: serverTimestamp(),
      });
      await refreshVendorIndex(selected.id);
      setNotice(
        `${task.type} allocation archived. Ab dobara allocate kar sakte hain.`,
      );
      await Promise.all([load(), loadProjectDetail(selected.id)]);
    } catch (error) {
      setNotice(error.message);
    } finally {
      setBusy("");
    }
  }

  async function removeFile(task, fileIndex) {
    try {
      assertCurrentPmc(selected);
    } catch (error) {
      return setNotice(error.message);
    }
    if (upper(task.pmcStatus || task.status) === "APPROVED") {
      return setNotice("Approved report locked hai.");
    }
    if (!window.confirm("Selected report ko task record se remove karein?"))
      return;
    const files = (task.files || []).filter((_, index) => index !== fileIndex);
    setBusy(task.id);
    try {
      await updateDoc(doc(db, NAFED_COLLECTIONS.workflowTasks, task.id), {
        files,
        status: files.length ? task.status : "ASSIGNED",
        pmcStatus: files.length ? task.pmcStatus : "ALLOCATED",
        updatedAt: serverTimestamp(),
      });
      setNotice("Report removed. Vendor revised report upload kar sakta hai.");
      await Promise.all([load(), loadProjectDetail(selected.id)]);
    } catch (error) {
      setNotice(error.message);
    } finally {
      setBusy("");
    }
  }

  async function cleanupDuplicates() {
    let user;
    try {
      user = assertCurrentPmc(selected);
    } catch (error) {
      return setNotice(error.message);
    }
    if (!selectedDuplicates.length) return;
    if (
      !window.confirm(
        `${selectedDuplicates.length} duplicate allocation record archive karein?`,
      )
    )
      return;
    setBusy("duplicates");
    try {
      for (const duplicate of selectedDuplicates) {
        if (upper(duplicate.pmcStatus || duplicate.status) !== "APPROVED") {
          await updateDoc(
            doc(db, NAFED_COLLECTIONS.workflowTasks, duplicate.id),
            {
              isDeleted: true,
              status: "ARCHIVED",
              archivedAt: serverTimestamp(),
              archivedByUid: user.uid,
              archivedByRole: "PMC",
              archiveReason: "DUPLICATE_STAGE_ALLOCATION",
              updatedAt: serverTimestamp(),
            },
          );
        }
      }
      await refreshVendorIndex(selected.id);
      setNotice("Duplicate non-approved allocations archived successfully.");
      await Promise.all([load(), loadProjectDetail(selected.id)]);
    } catch (error) {
      setNotice(error.message);
    } finally {
      setBusy("");
    }
  }

  return (
    <section className="nwf">
      <style>{CSS + NEW_CSS}</style>
      <header>
        <div>
          <small>NAFED · ALLOCATED PMC TECHNICAL DESK</small>
          <h2>Pre-Tender Vendor Workflow</h2>
          <p>Only your allocated PACS with fully executed MOU.</p>
        </div>
        <button onClick={load}>Refresh</button>
      </header>
      {notice && <div className="notice">{notice}</div>}
      <div className="stats">
        <div>
          <span>Allocated PACS</span>
          <b>{projects.length}</b>
        </div>
        <div>
          <span>Active Work Orders</span>
          <b>
            {
              projects.filter((item) => {
                const status = upper(item.technicalWorkflowStatus);
                return status && !status.includes("READY");
              }).length
            }
          </b>
        </div>
        <div>
          <span>Approved Vendors</span>
          <b>{vendors.length}</b>
        </div>
        <div>
          <span>Ready for Tender</span>
          <b>
            {
              projects.filter((p) => p.technicalStage === "READY_FOR_TENDER")
                .length
            }
          </b>
        </div>
      </div>
      <section className="pacs-register">
        <div className="pacs-register-head">
          <div>
            <small>PMC ALLOCATED PACS MASTER REGISTER</small>
            <h3>Select a PACS to open its technical workflow</h3>
          </div>
          <b>{filteredProjects.length} RECORD(S)</b>
        </div>
        <div className="pacs-filters">
          <input
            value={pacsSearch}
            onChange={(event) => {
              setPacsSearch(event.target.value);
              setPage(1);
            }}
            placeholder="Search PACS name, application ID, district, taluka or village..."
          />
          <select
            value={pacsDistrict}
            onChange={(event) => {
              setPacsDistrict(event.target.value);
              setPage(1);
            }}
          >
            <option value="ALL">All Districts</option>
            {districts.map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </select>
          <select
            value={pacsStatus}
            onChange={(event) => {
              setPacsStatus(event.target.value);
              setPage(1);
            }}
          >
            <option value="ALL">All Workflow Status</option>
            <option value="IN_PROGRESS">Technical Work in Progress</option>
            <option value="READY">Ready for Tender</option>
          </select>
        </div>
        <div className="pacs-table-wrap">
          <table className="pacs-table">
            <thead>
              <tr>
                <th>Sr.</th>
                <th>PACS / Application</th>
                <th>Location</th>
                <th>Capacity</th>
                <th>Technical Stage</th>
                <th>Workflow Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" className="empty">
                    Loading allocated PACS register...
                  </td>
                </tr>
              ) : (
                pagedProjects.map((project, index) => (
                  <tr key={project.id}>
                    <td>{(safePage - 1) * PAGE_SIZE + index + 1}</td>
                    <td>
                      <strong>{project.pacsName || "PACS / Society"}</strong>
                      <small>{project.applicationId || project.id}</small>
                    </td>
                    <td>
                      <b>{project.district || "-"}</b>
                      <small>
                        {project.locationVillage || project.village || "-"} ·{" "}
                        {project.taluka || "-"}
                      </small>
                    </td>
                    <td>
                      <b>
                        {Number(project.capacityMt || 0).toLocaleString(
                          "en-IN",
                        )}{" "}
                        MT
                      </b>
                    </td>
                    <td>
                      <span className="registry-stage">
                        {stageLabel(
                          project.currentTechnicalStage || "NOT STARTED",
                        )}
                      </span>
                    </td>
                    <td>
                      <span className="registry-status">
                        {String(
                          project.technicalWorkflowStatus || "PENDING",
                        ).replaceAll("_", " ")}
                      </span>
                    </td>
                    <td>
                      <button
                        className="open-workflow"
                        disabled={projectBusy === project.id}
                        onClick={() => openProject(project.id)}
                      >
                        {projectBusy === project.id
                          ? "Opening..."
                          : "Open Workflow"}
                      </button>
                    </td>
                  </tr>
                ))
              )}
              {!loading && !pagedProjects.length && (
                <tr>
                  <td colSpan="7" className="empty">
                    No PACS matches the selected filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="pacs-pagination">
          <span>
            Showing{" "}
            {filteredProjects.length ? (safePage - 1) * PAGE_SIZE + 1 : 0}–
            {Math.min(safePage * PAGE_SIZE, filteredProjects.length)} of{" "}
            {filteredProjects.length}
          </span>
          <div>
            <button disabled={safePage === 1} onClick={() => setPage(1)}>
              First
            </button>
            <button
              disabled={safePage === 1}
              onClick={() => setPage((old) => Math.max(1, old - 1))}
            >
              Previous
            </button>
            <b>
              Page {safePage} of {totalPages}
            </b>
            <button
              disabled={safePage === totalPages}
              onClick={() => setPage((old) => Math.min(totalPages, old + 1))}
            >
              Next
            </button>
            <button
              disabled={safePage === totalPages}
              onClick={() => setPage(totalPages)}
            >
              Last
            </button>
          </div>
        </div>
      </section>

      {selected && (
        <div className="workflow-modal" role="dialog" aria-modal="true">
          <button
            className="modal-backdrop"
            aria-label="Close workflow"
            onClick={() => setSelectedId("")}
          />
          <main className="workflow-window">
            <div className="window-bar">
              <div>
                <small>NAFED TECHNICAL CASE WINDOW</small>
                <b>{selected.pacsName}</b>
              </div>
              <button onClick={() => setSelectedId("")}>× Close</button>
            </div>
            {projectBusy === selected.id ? (
              <div className="empty">Loading PACS technical workflow...</div>
            ) : (
              <>
                <div className="project-head">
                  <div>
                    <small>FULLY EXECUTED MOU · NAFED</small>
                    <h3>{selected.pacsName}</h3>
                    <p>
                      {selected.locationVillage}, {selected.taluka},{" "}
                      {selected.district}, {selected.state}
                    </p>
                  </div>
                  {selected.googleMapsUrl ? (
                    <a
                      href={selected.googleMapsUrl}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Open Verified Site Location
                    </a>
                  ) : (
                    <span>Site location not available</span>
                  )}
                </div>
                <div className="timeline">
                  {WORK_STAGES.map((stage, i) => {
                    const task = selectedTasks.find(
                      (t) => upper(t.type) === stage.key,
                    );
                    const unlocked = unlockedStages.some(
                      (item) => item.key === stage.key,
                    );
                    return (
                      <div
                        key={stage.key}
                        className={statusClass(task?.pmcStatus || task?.status)}
                      >
                        <b>{i + 1}</b>
                        <span>{stage.label}</span>
                        <small>
                          {task?.pmcStatus ||
                            task?.status ||
                            (unlocked ? "UNLOCKED" : "LOCKED")}
                        </small>
                      </div>
                    );
                  })}
                </div>
                {!!selectedDuplicates.length && (
                  <div className="duplicate-alert">
                    <div>
                      <b>
                        {selectedDuplicates.length} duplicate allocation
                        detected
                      </b>
                      <span>
                        Table mein sirf latest valid stage record dikhaya gaya
                        hai.
                      </span>
                    </div>
                    <button
                      disabled={busy === "duplicates"}
                      onClick={cleanupDuplicates}
                    >
                      Clean Duplicates
                    </button>
                  </div>
                )}
                {!!unlockedStages.length && (
                  <section className="allocation-zone">
                    <div className="zone-title">
                      <div>
                        <small>AVAILABLE FOR ALLOCATION</small>
                        <h3>
                          {unlockedStages.length > 1
                            ? "Parallel Initial Activities"
                            : "Next Workflow Activity"}
                        </h3>
                        <p>
                          Survey and Soil Test can run together. Later stages
                          unlock one-by-one.
                        </p>
                      </div>
                    </div>
                    <div className="allocation-grid">
                      {unlockedStages.map((stage) => {
                        const stageVendors = vendorsForStage(stage.key);
                        return (
                          <div className="allocation-card" key={stage.key}>
                            <div>
                              <small>UNLOCKED</small>
                              <h4>{stage.label}</h4>
                            </div>
                            <select
                              value={vendorChoice[stage.key] || ""}
                              onChange={(e) =>
                                setVendorChoice((old) => ({
                                  ...old,
                                  [stage.key]: e.target.value,
                                }))
                              }
                            >
                              <option value="">Select approved vendor</option>
                              {stageVendors.map((v) => (
                                <option key={v.id} value={v.id}>
                                  {v.firmName || v.name}
                                </option>
                              ))}
                            </select>
                            <button
                              disabled={
                                Boolean(busy) || !vendorChoice[stage.key]
                              }
                              onClick={() => allocate(stage)}
                            >
                              {busy === `allocate-${stage.key}`
                                ? "Allocating..."
                                : "Allocate Work"}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </section>
                )}
                <div className="register-head">
                  <div>
                    <small>OFFICIAL TECHNICAL WORK REGISTER</small>
                    <h3>Vendor Assignment, Submission & PMC Decision</h3>
                  </div>
                  <span>{selectedTasks.length} WORK ORDER(S)</span>
                </div>
                <div className="task-table">
                  <table>
                    <colgroup>
                      <col style={{ width: "11%" }} />
                      <col style={{ width: "26%" }} />
                      <col style={{ width: "13%" }} />
                      <col style={{ width: "12%" }} />
                      <col style={{ width: "19%" }} />
                      <col style={{ width: "19%" }} />
                    </colgroup>
                    <thead>
                      <tr>
                        <th>Work Stage</th>
                        <th>Authorised Vendor</th>
                        <th>Report Files</th>
                        <th>Current Status</th>
                        <th>Schedule & PMC Remarks</th>
                        <th>Official Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedTasks.map((t) => (
                        <tr key={t.id}>
                          <td className="stage-cell">
                            <span className="stage-code">{upper(t.type)}</span>
                            <strong className="stage-name">
                              {stageLabel(t.type)}
                            </strong>
                          </td>
                          <td className="vendor-cell">
                            {editingId === t.id ? (
                              <select
                                value={editDraft.vendorId}
                                onChange={(e) =>
                                  setEditDraft((old) => ({
                                    ...old,
                                    vendorId: e.target.value,
                                  }))
                                }
                              >
                                {vendorsForStage(upper(t.type)).map((v) => (
                                  <option key={v.id} value={v.id}>
                                    {v.firmName || v.name}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <>
                                <b>{t.vendorName || t.vendorId}</b>
                                <small>{t.vendorEmail || ""}</small>
                              </>
                            )}
                          </td>
                          <td>
                            {(t.files || []).map((f, i) => (
                              <div className="file-line" key={`${f.url}-${i}`}>
                                <a
                                  href={f.url}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  {f.name || `File ${i + 1}`}
                                </a>
                                {upper(t.pmcStatus || t.status) !==
                                  "APPROVED" && (
                                  <button
                                    title="Remove report"
                                    onClick={() => removeFile(t, i)}
                                  >
                                    ×
                                  </button>
                                )}
                              </div>
                            ))}
                            {!(t.files || []).length && (
                              <em>No report uploaded</em>
                            )}
                          </td>
                          <td>
                            <span
                              className={`task-status ${statusClass(
                                t.pmcStatus || t.status,
                              )}`}
                            >
                              {String(t.pmcStatus || t.status).replaceAll(
                                "_",
                                " ",
                              )}
                            </span>
                          </td>
                          <td>
                            {editingId === t.id ? (
                              <div className="edit-fields">
                                <input
                                  type="datetime-local"
                                  value={editDraft.plannedDate}
                                  onChange={(e) =>
                                    setEditDraft((old) => ({
                                      ...old,
                                      plannedDate: e.target.value,
                                    }))
                                  }
                                />
                                <input
                                  value={editDraft.pmcRemarks}
                                  onChange={(e) =>
                                    setEditDraft((old) => ({
                                      ...old,
                                      pmcRemarks: e.target.value,
                                    }))
                                  }
                                  placeholder="PMC remarks"
                                />
                              </div>
                            ) : (
                              <>
                                <b>
                                  {t.plannedDate ||
                                    t.plannedDateTime ||
                                    "Not scheduled"}
                                </b>
                                <small>{t.pmcRemarks || "No remarks"}</small>
                              </>
                            )}
                          </td>
                          <td className="row-actions">
                            <div className="action-grid">
                              {editingId === t.id ? (
                                <>
                                  <button
                                    className="approve"
                                    onClick={() => saveEdit(t)}
                                  >
                                    Save Changes
                                  </button>
                                  <button
                                    className="neutral"
                                    onClick={() => setEditingId("")}
                                  >
                                    Cancel
                                  </button>
                                </>
                              ) : (
                                upper(t.pmcStatus || t.status) !==
                                  "APPROVED" && (
                                  <>
                                    <button
                                      className="edit"
                                      onClick={() => startEdit(t)}
                                    >
                                      Edit
                                    </button>
                                    <button
                                      className="delete"
                                      onClick={() => removeTask(t)}
                                    >
                                      Delete
                                    </button>
                                  </>
                                )
                              )}
                              <button
                                className="approve"
                                disabled={
                                  busy === t.id ||
                                  !(t.files || []).length ||
                                  upper(t.pmcStatus || t.status) === "APPROVED"
                                }
                                onClick={() => decide(t, "APPROVED")}
                              >
                                Approve Report
                              </button>
                              <button
                                className="query"
                                disabled={
                                  busy === t.id ||
                                  upper(t.pmcStatus || t.status) === "APPROVED"
                                }
                                onClick={() => decide(t, "QUERY_RAISED")}
                              >
                                Raise Query
                              </button>
                              <button
                                className="reject"
                                disabled={
                                  busy === t.id ||
                                  upper(t.pmcStatus || t.status) === "APPROVED"
                                }
                                onClick={() => decide(t, "REJECTED")}
                              >
                                Reject
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="table-note">
                    <span>
                      Reports can be approved only after the vendor uploads a
                      valid file.
                    </span>
                    <span>
                      Approved records remain locked for official audit.
                    </span>
                  </div>
                </div>
              </>
            )}
          </main>
        </div>
      )}
    </section>
  );
}

const CSS = `.nwf{font-family:Inter,"Segoe UI",Arial;color:#10233e}.nwf>header{display:flex;justify-content:space-between;align-items:center;padding:20px;border-radius:16px;background:linear-gradient(110deg,#102e59,#17613f);color:#fff}.nwf header small{color:#bfe9ca;font-weight:900}.nwf h2{margin:5px 0}.nwf header p{margin:0;color:#dcece3}.nwf button,.nwf a{cursor:pointer}.nwf>header button,.project-head a,.allocation button{border:0;border-radius:7px;padding:10px 14px;background:#fff;color:#143965;font-weight:900}.notice{margin:12px 0;padding:11px;border:1px solid #91c7a0;background:#edf8f0}.stats{display:grid;grid-template-columns:repeat(4,1fr);gap:10px;margin:12px 0}.stats div{padding:13px;border:1px solid #aeb9c8;background:#fff}.stats span,.stats b{display:block}.stats span{font-size:11px;color:#64748b}.stats b{font-size:22px;margin-top:5px}.body{display:grid;grid-template-columns:280px 1fr;border:1px solid #172033;background:#fff}.body aside{padding:12px;border-right:1px solid #172033;background:#f4f7fa}.body aside>button{width:100%;display:grid;text-align:left;padding:11px;margin:6px 0;border:1px solid #aeb9c8;background:#fff}.body aside>button.active{border:2px solid #1d6b43;background:#edf8f0}.body aside span,.body aside small{margin-top:3px;color:#64748b}.body main{padding:14px;overflow:hidden}.project-head{display:flex;justify-content:space-between;align-items:center;padding:16px;background:#13365f;color:#fff}.project-head h3{margin:4px 0}.project-head p{margin:0}.timeline{display:grid;grid-template-columns:repeat(5,1fr);gap:7px;margin:12px 0}.timeline>div{padding:10px;border:1px solid #aeb9c8;text-align:center}.timeline b,.timeline span,.timeline small{display:block}.timeline b{margin:auto;width:27px;height:27px;padding-top:5px;border-radius:50%;background:#94a3b8;color:#fff}.timeline span{margin:5px 0;font-weight:800}.timeline .ok{background:#e9f7ed}.timeline .ok b{background:#17834b}.timeline .review{background:#eef5ff}.timeline .pending{background:#f6f7f9}.timeline .bad{background:#fff0ef}.allocation{display:grid;grid-template-columns:1fr 300px auto;align-items:center;gap:10px;padding:13px;border:1px solid #b8c2cf;background:#f5f8fb}.allocation h3{margin:3px 0}.allocation p{margin:0}.allocation select{padding:10px}.task-table{overflow:auto;margin-top:12px;border:1px solid #172033}.task-table table{width:100%;min-width:850px;border-collapse:collapse}.task-table th,.task-table td{padding:10px;border:1px solid #172033;text-align:left}.task-table th{background:#dfe7ef}.task-table td a{display:block}.task-table td button{border:0;padding:7px;margin:2px;background:#168149;color:#fff}.task-table .query{background:#c87413}.task-table .reject{background:#b42318}.task-table span.ok,.task-table span.review,.task-table span.pending,.task-table span.bad{padding:5px 8px;border-radius:999px}.empty{padding:50px;text-align:center}@media(max-width:900px){.stats{grid-template-columns:repeat(2,1fr)}.body{grid-template-columns:1fr}.body aside{border-right:0}.timeline{grid-template-columns:1fr}.allocation{grid-template-columns:1fr}}`;
